import { CREATE_NEW_INGREDIENT_ID, DEFAULT_INGREDIENT_SELLABLE, DELETED_INGREDIENT_ID, IngredientLongDisplayUnits, IngredientUnits, USE_DEFAULT_INGREDIENT_ID, } from './IngredientEntity';
export const defaultIngredient = {
    priceFluctuation: 0,
    priceFluctuationPercentage: 0,
    priceFluctuationImpact: 0,
    organizationId: 'new',
    name: '',
    unit: IngredientUnits.KG,
    pricePerUnit: 0,
    currentAveragePricePerUnit: 0,
    priceHistoryLength: 0,
    isGenericPrice: false,
    category: null,
    sellable: DEFAULT_INGREDIENT_SELLABLE,
};
export const clearGenericPriceWhenPriced = (data) => (data.pricePerUnit === undefined ? data : { ...data, isGenericPrice: false });
export const resolveIngredientSellable = (sellable) => {
    if (!sellable) {
        return { ...DEFAULT_INGREDIENT_SELLABLE };
    }
    return {
        enabled: sellable.enabled ?? DEFAULT_INGREDIENT_SELLABLE.enabled,
        pvp: sellable.pvp ?? DEFAULT_INGREDIENT_SELLABLE.pvp,
        pvpGlass: sellable.pvpGlass ?? DEFAULT_INGREDIENT_SELLABLE.pvpGlass,
        pvpBottle: sellable.pvpBottle ?? DEFAULT_INGREDIENT_SELLABLE.pvpBottle,
    };
};
export const normalizeIngredientSellable = (sellable) => resolveIngredientSellable(sellable);
export const mapUnitToIngredientUnit = (unit) => {
    if (unit === 'g')
        return IngredientUnits.KG;
    if (unit === 'ml')
        return IngredientUnits.L;
    if (unit === 'kg' || unit === 'l' || unit === 'ud')
        return unit;
    return undefined;
};
export const getDisplayUnit = (quantity, unit) => {
    if (unit === 'kg' && quantity < 1) {
        return 'g';
    }
    if (unit === 'l' && quantity < 1) {
        return 'ml';
    }
    return unit;
};
export const getLongUnit = (quantity, unit) => {
    if (unit === 'kg')
        return quantity === 1 ? IngredientLongDisplayUnits.KG : IngredientLongDisplayUnits.KG_PLURAL;
    if (unit === 'l')
        return quantity === 1 ? IngredientLongDisplayUnits.L : IngredientLongDisplayUnits.L_PLURAL;
    if (unit === 'ud')
        return quantity === 1 ? IngredientLongDisplayUnits.UD : IngredientLongDisplayUnits.UD_PLURAL;
    return quantity === 1 ? IngredientLongDisplayUnits.UD : IngredientLongDisplayUnits.UD_PLURAL;
};
export const getLongDisplayUnit = (displayQuantity, unit) => {
    const displayUnit = getDisplayUnit(displayQuantity, unit);
    if (displayUnit === 'kg')
        return displayQuantity === 1 ? IngredientLongDisplayUnits.KG : IngredientLongDisplayUnits.KG_PLURAL;
    if (displayUnit === 'l')
        return displayQuantity === 1 ? IngredientLongDisplayUnits.L : IngredientLongDisplayUnits.L_PLURAL;
    if (displayUnit === 'ud')
        return displayQuantity === 1 ? IngredientLongDisplayUnits.UD : IngredientLongDisplayUnits.UD_PLURAL;
    if (displayUnit === 'g')
        return displayQuantity === 1 ? IngredientLongDisplayUnits.G : IngredientLongDisplayUnits.G_PLURAL;
    if (displayUnit === 'ml')
        return displayQuantity === 1 ? IngredientLongDisplayUnits.ML : IngredientLongDisplayUnits.ML_PLURAL;
    return displayQuantity === 1 ? IngredientLongDisplayUnits.UD : IngredientLongDisplayUnits.UD_PLURAL;
};
export const extractQuantity = (quantity, unit) => {
    if ((unit === 'kg' && quantity < 1) || (unit === 'l' && quantity < 1)) {
        return quantity * 1000;
    }
    return quantity;
};
export const mapUnits = (displayUnit, quantity) => {
    if (['g', 'ml'].includes(displayUnit)) {
        return { quantity: quantity / 1000, unit: displayUnit === 'g' ? 'kg' : 'l' };
    }
    return { quantity, unit: displayUnit };
};
export const changeUnit = (currentQuantity, currentDisplayUnit, newDisplayUnit) => {
    console.log('Changing unit', { currentQuantity, currentDisplayUnit, newDisplayUnit });
    let newQuantity = currentQuantity;
    let newUnit = currentDisplayUnit;
    if (['kg', 'l', 'ud'].includes(currentDisplayUnit) && ['g', 'ml'].includes(newDisplayUnit)) {
        newQuantity = currentQuantity / 1000;
        newUnit = newDisplayUnit === 'g' ? 'kg' : 'l';
    }
    else if (['g', 'ml'].includes(currentDisplayUnit) && ['kg', 'l', 'ud'].includes(newDisplayUnit)) {
        newQuantity = currentQuantity * 1000;
        newUnit = newDisplayUnit;
    }
    else {
        newUnit = mapUnitToIngredientUnit(newDisplayUnit);
    }
    return { quantity: newQuantity, unit: newUnit };
};
export const isRealIngredientId = (id) => !!(id && id !== CREATE_NEW_INGREDIENT_ID && id !== DELETED_INGREDIENT_ID && id !== USE_DEFAULT_INGREDIENT_ID);
/**
 * Reference price per unit is the price per unit that is used to compute the price fluctuations and impact.
 * We use the most extreme price in comparison to the current price. ( Best if price increased, worst if price decreased)
 */
export const computeIngredientReferencePricePerUnit = ({ pricePerUnit, bestPrice, worstPrice, }) => {
    const shouldUseBestPrice = bestPrice != null && pricePerUnit > bestPrice.pricePerUnit;
    return shouldUseBestPrice ? bestPrice.pricePerUnit : (worstPrice?.pricePerUnit ?? pricePerUnit);
};
export const computeIngredientPriceFluctuation = ({ pricePerUnit, bestPrice, worstPrice, }) => {
    const referencePrice = computeIngredientReferencePricePerUnit({ pricePerUnit, bestPrice, worstPrice });
    if (!referencePrice)
        return 0;
    return pricePerUnit - referencePrice;
};
export const computeIngredientPriceFluctuationPercentage = (ingredient) => {
    const referencePrice = computeIngredientReferencePricePerUnit(ingredient) ?? 0;
    if (referencePrice <= 0)
        return 0;
    const fluctuationPercentage = (computeIngredientPriceFluctuation(ingredient) / referencePrice) * 100;
    return fluctuationPercentage;
};
export const computeIngredientPriceFluctuationImpact = ({ pricePerUnit, bestPrice, worstPrice, monthlyPurchases, }) => {
    if (!monthlyPurchases)
        return 0;
    const referencePrice = computeIngredientReferencePricePerUnit({ pricePerUnit, bestPrice, worstPrice });
    if (!referencePrice)
        return 0;
    return (pricePerUnit - referencePrice) * monthlyPurchases.averageInUnit;
};
export const formatIngredientCost = (cost) => (cost > 0 && cost < 0.01 ? '<0.01' : cost.toFixed(2));
//# sourceMappingURL=IngredientEntityService.js.map